import Vue from 'vue'
import LearnService from '@/services/LearnService'

const createLearnModule = () => ({
  namespaced: true,
  state: {
    filter: '',
    page: 1,
    pages: 1,
    pageSize: 24,
    total: 0,
    working: false,
    documents: [],
    userItems: [],
    sources: [],
    contentTypes: [],
    collections: [],
    userCollections: [],
    expandedCollections: [],
    viewRows: false,
    viewAdvancedFilters: false,
    collectionCreators: [],
    collectionLevels: [],
    collectionDomains: [],
    advancedFilters: {
      time: null,
      source: null,
      code: null,
      contentType: null,
      similar: null,
      docId: null,
      collectionSearch: null,
      collectionLevel: null,
      collectionDomain: null,
      collectionCreator: null,
      contextBasedSearch: true
    },
    isLoading: true
  },
  actions: {
    getDocuments: async ({ commit, state }) => {
      commit('SET_IS_LOADING', true)
      const { results } = await LearnService.getDocuments(
        state.filter,
        state.page,
        state.pageSize,
        state.advancedFilters
      )
      const maxTotal = results.pages * state.pageSize
      commit('SET_DOCUMENTS', { documents: results.documents })
      commit('SET_PAGES', { pages: results.pages })
      commit('SET_TOTAL', { total: results.total > maxTotal ? maxTotal : results.total })
      commit('SET_IS_LOADING', false)
    },
    getUserItems: async ({ commit }) => {
      const { userItems } = await LearnService.getUserItems()
      commit('SET_USER_ITEMS', { userItems })
    },
    getSources: async ({ commit }) => {
      const { sources } = await LearnService.getSources()
      commit('SET_SOURCES', { sources })
    },
    getContentTypes: async ({ commit }) => {
      const { contentTypes } = await LearnService.getContentTypes()
      commit('SET_CONTENT_TYPES', { contentTypes })
    },
    getCollections: async ({ commit, state }) => {
      const { learnCollections } = await LearnService.getCollections(
        state.filter,
        state.page,
        state.pageSize,
        state.advancedFilters
      )
      commit('SET_COLLECTIONS', { collections: learnCollections })
    },
    getCollection: async ({ commit }, { collectionId }) => {
      const { learnCollection } = await LearnService.getCollection(collectionId)
      commit('DELETE_COLLECTION', { collectionId })
      commit('SET_COLLECTION', { collection: learnCollection })
    },
    getUserCollections: async ({ commit }) => {
      const { collections: userCollections } = await LearnService.getUserCollections()
      commit('SET_USER_COLLECTIONS', { userCollections })
    },
    deleteCollection: async ({ commit }, { collectionId }) => {
      await LearnService.deleteCollection(collectionId)
      commit('DELETE_COLLECTION', { collectionId })
    },
    getCollectionLevels: async ({ commit }) => {
      const { levels: collectionLevels } = await LearnService.getCollectionLevels()
      commit('SET_COLLECTION_LEVELS', { collectionLevels })
    },
    getCollectionDomains: async ({ commit }) => {
      const { domains: collectionDomains } = await LearnService.getCollectionDomains()
      commit('SET_COLLECTION_DOMAINS', { collectionDomains })
    },
    getCollectionCreators: async ({ commit }) => {
      const { creators: collectionCreators } = await LearnService.getCollectionCreators()
      commit('SET_COLLECTION_CREATORS', { collectionCreators })
    },
    setPageSize: ({ commit }, pageSize) => {
      commit('SET_PAGE_SIZE', { pageSize })
    },
    setPage: async ({ dispatch, commit }, page) => {
      commit('SET_PAGE', { page })
      commit('SET_WORKING', true)
      await dispatch('getDocuments')
      commit('SET_WORKING', false)
    },
    setFilter: async ({ commit, dispatch }, { filter, noFetch }) => {
      commit('SET_FILTER', { filter })
      commit('SET_PAGE', { page: 1 })
      commit('SET_WORKING', true)
      if (!noFetch && filter.startsWith('collection')) {
        await dispatch('getCollections')
      } else if (!noFetch) {
        await dispatch('getDocuments')
      }
      commit('SET_WORKING', false)
    },
    toggleViewAdvancedFilters: async ({ commit, state }) => {
      commit('SET_VIEW_ADVANCED_FILTERS', !state.viewAdvancedFilters)
    },
    toggleViewRows: async ({ commit, state }) => {
      commit('SET_VIEW_ROWS', !state.viewRows)
    },
    setDocumentCompleted: async ({ commit, state }, { documentGuid, completed }) => {
      const update = await LearnService.updateDocument(documentGuid, { completed })
      commit('UPDATE_DOCUMENT', { documentGuid, update: update.document })
    },
    setDocumentFavourite: async ({ commit, state }, { documentGuid, favourite }) => {
      const update = await LearnService.updateDocument(documentGuid, { favourite })
      commit('UPDATE_DOCUMENT', { documentGuid, update: update.document })
    },
    setDocumentNotes: async ({ commit, state }, { documentGuid, notes }) => {
      const update = await LearnService.updateDocument(documentGuid, { notes })
      commit('UPDATE_DOCUMENT', { documentGuid, update: update.document })
    },
    setDocumentTags: async ({ commit, state }, { documentGuid, tags }) => {
      const update = await LearnService.updateDocument(documentGuid, { tags })
      commit('UPDATE_DOCUMENT', { documentGuid, update: update.document })
    },
    setAdvancedFilter: async ({ commit, dispatch }, { advancedFilter, noFetch }) => {
      commit('SET_WORKING', true)
      commit('SET_PAGE', { page: 1 })
      commit('SET_ADVANCED_FILTER', { advancedFilter })
      if (!noFetch) {
        await dispatch('getDocuments')
      }
      commit('SET_WORKING', false)
    }
  },
  getters: {
    prevDisabled: (state) => state.page === 1,
    nextDisabled(state) {
      return state.pageSize * state.page >= state.total
    },
    documents: (state) => state.documents,
    userItems: (state) => state.userItems,
    sources: (state) => state.sources,
    contentTypes: (state) => state.contentTypes,
    collectionCreators: (state) => state.collectionCreators,
    collectionLevels: (state) => state.collectionLevels,
    collectionDomains: (state) => state.collectionDomains,
    total: (state) => state.total,
    pages: (state) => state.pages,
    page: (state) => state.page,
    pageSize: (state) => state.pageSize,
    working: (state) => state.working,
    viewRows: (state) => state.viewRows,
    viewAdvancedFilters: (state) => state.viewAdvancedFilters,
    advancedFilters: (state) => state.advancedFilters,
    isLoading: (state) => state.isLoading,
    collections: (state) => state.collections,
    collectionById: (state) => (collectionId) => {
      return state.expandedCollections.find((collection) => collection._id === collectionId)
    },
    userCollections: (state) => state.userCollections
  },
  mutations: {
    SET_DOCUMENTS: (state, { documents }) => {
      state.documents = documents
    },
    SET_USER_ITEMS: (state, { userItems }) => {
      state.userItems = userItems
    },
    SET_SOURCES: (state, { sources }) => {
      state.sources = sources.sort((a, b) => a.name.localeCompare(b.name))
    },
    SET_CONTENT_TYPES: (state, { contentTypes }) => {
      state.contentTypes = contentTypes
    },
    SET_COLLECTION_CREATORS: (state, { collectionCreators }) => {
      state.collectionCreators = collectionCreators
    },
    SET_COLLECTION_LEVELS: (state, { collectionLevels }) => {
      state.collectionLevels = collectionLevels
    },
    SET_COLLECTION_DOMAINS: (state, { collectionDomains }) => {
      state.collectionDomains = collectionDomains
    },
    SET_COLLECTIONS: (state, { collections }) => {
      state.collections = collections
    },
    SET_COLLECTION: (state, { collection }) => {
      state.expandedCollections.push(collection)
    },
    SET_USER_COLLECTIONS: (state, { userCollections }) => {
      state.userCollections = userCollections
    },
    DELETE_COLLECTION: (state, { collectionId }) => {
      state.collections = state.collections.filter((collection) => collection._id !== collectionId)
      state.expandedCollections = state.expandedCollections.filter(
        (collection) => collection._id !== collectionId
      )
    },
    SET_PAGE: (state, { page }) => {
      state.page = page
    },
    SET_PAGES: (state, { pages }) => {
      state.pages = pages
    },
    SET_TOTAL: (state, { total }) => {
      state.total = total
    },
    SET_WORKING: (state, working) => {
      state.working = working
    },
    SET_FILTER: (state, { filter }) => {
      state.filter = filter
    },
    SET_VIEW_ROWS: (state, viewRows) => {
      state.viewRows = viewRows
    },
    SET_VIEW_ADVANCED_FILTERS: (state, viewAdvancedFilters) => {
      state.viewAdvancedFilters = viewAdvancedFilters
    },
    SET_ADVANCED_FILTER: (state, { advancedFilter }) => {
      if (!advancedFilter.key) {
        throw new Error('Invalid signature')
      }

      Vue.set(state.advancedFilters, advancedFilter.key, advancedFilter.value)
    },
    UPDATE_DOCUMENT: (state, { documentGuid, update }) => {
      const document = state.documents.find((d) => d.guid === documentGuid)
      if (document) {
        Object.entries(update).forEach(([key, value]) => {
          Vue.set(document.userItem, key, value)
        })
      }

      const userItem = state.userItems.find((d) => d.guid === documentGuid)
      if (userItem) {
        Object.entries(update).forEach(([key, value]) => {
          Vue.set(userItem.userItem, key, value)
        })
      }
    },
    SET_IS_LOADING: (state, isLoading) => {
      state.isLoading = isLoading
    }
  }
})

export default createLearnModule
